import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Chip, 
  Tooltip, 
  IconButton, 
  Collapse,
  Paper
} from '@mui/material';
import { 
  DeveloperMode as DevIcon,
  Storage as DatabaseIcon,
  AccountTree as BranchIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon
} from '@mui/icons-material';
import axios from '../utils/axiosConfig';

interface DevInfo {
  environment: string;
  isProduction: boolean;
  git: {
    branch: string;
    commit: string;
  } | null;
  database: {
    name: string;
    host?: string;
    port?: string | number;
    readyState: number;
  };
  server: {
    port: string | number;
    allowedOrigins?: string;
  };
}

const DevInfo: React.FC = () => {
  const [devInfo, setDevInfo] = useState<DevInfo | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [shouldShow, setShouldShow] = useState(false);

  useEffect(() => {
    const fetchDevInfo = async () => {
      try {
        const response = await axios.get('/api/dev-info');
        setDevInfo(response.data);
        setError(null);
        
        // Primär auf Backend-Response basieren
        const backendSaysProduction = response.data.isProduction;
        const forceShow = process.env.REACT_APP_SHOW_DEV_INFO === 'true';
        
        // Nur anzeigen wenn:
        // 1. Backend sagt "nicht production" ODER
        // 2. Explizit per Umgebungsvariable erzwungen
        setShouldShow(!backendSaysProduction || forceShow);
        
      } catch (err: any) {
        console.warn('Dev-Info konnte nicht abgerufen werden:', err);
        setError(err.response?.data?.message || 'Verbindung fehlgeschlagen');
        
        // Bei Fehler nur anzeigen wenn explizit erzwungen
        setShouldShow(process.env.REACT_APP_SHOW_DEV_INFO === 'true');
      }
    };

    fetchDevInfo();
    // Info alle 30 Sekunden aktualisieren
    const interval = setInterval(fetchDevInfo, 30000);

    return () => clearInterval(interval);
  }, []);

  // Nicht anzeigen wenn shouldShow false ist oder wenn weder devInfo noch error vorhanden sind
  if (!shouldShow || (!devInfo && !error)) {
    return null;
  }

  const getDbStatusColor = (readyState: number) => {
    switch (readyState) {
      case 1: return 'success'; // connected
      case 2: return 'warning'; // connecting
      case 3: return 'warning'; // disconnecting
      default: return 'error'; // disconnected
    }
  };

  const getDbStatusText = (readyState: number) => {
    switch (readyState) {
      case 1: return 'Verbunden';
      case 2: return 'Verbindet...';
      case 3: return 'Trennt...';
      default: return 'Getrennt';
    }
  };

  return (
    <Box sx={{ 
      position: 'fixed', 
      top: 0, 
      left: 0, 
      right: 0, 
      zIndex: 9999,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      color: 'white',
      py: 0.5,
      px: 2,
      borderBottom: '2px solid #ff6b35'
    }}>
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        maxWidth: '100%'
      }}>
        {/* Basis-Info - immer sichtbar */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Chip
            icon={<DevIcon />}
            label="DEV"
            size="small"
            color="warning"
            variant="filled"
          />
          
          {devInfo && (
            <>
              {devInfo.git && (
                <Tooltip title={`Git Branch: ${devInfo.git.branch} (${devInfo.git.commit})`}>
                  <Chip
                    icon={<BranchIcon />}
                    label={devInfo.git.branch}
                    size="small"
                    sx={{ backgroundColor: '#4caf50', color: 'white' }}
                  />
                </Tooltip>
              )}
              
              <Tooltip title={`Database: ${devInfo.database.name} (${getDbStatusText(devInfo.database.readyState)})`}>
                <Chip
                  icon={<DatabaseIcon />}
                  label={devInfo.database.name}
                  size="small"
                  color={getDbStatusColor(devInfo.database.readyState)}
                />
              </Tooltip>

              {devInfo.isProduction && (
                <Chip
                  label="PRODUCTION"
                  size="small"
                  sx={{ backgroundColor: '#f44336', color: 'white', fontWeight: 'bold' }}
                />
              )}
            </>
          )}

          {error && (
            <Chip
              label={`Fehler: ${error}`}
              size="small"
              color="error"
            />
          )}
        </Box>

        {/* Expand/Collapse Button */}
        {devInfo && (
          <IconButton 
            size="small" 
            onClick={() => setExpanded(!expanded)}
            sx={{ color: 'white' }}
          >
            {expanded ? <CollapseIcon /> : <ExpandIcon />}
          </IconButton>
        )}
      </Box>

      {/* Erweiterte Informationen */}
      <Collapse in={expanded}>
        {devInfo && (
          <Paper sx={{ 
            mt: 1, 
            p: 2, 
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(5px)'
          }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
              {devInfo.git && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>Git</Typography>
                  <Typography variant="body2">Branch: {devInfo.git.branch}</Typography>
                  <Typography variant="body2">Commit: {devInfo.git.commit}</Typography>
                </Box>
              )}
              
              <Box>
                <Typography variant="subtitle2" gutterBottom>Datenbank</Typography>
                <Typography variant="body2">Name: {devInfo.database.name}</Typography>
                {devInfo.database.host && devInfo.database.port && (
                  <Typography variant="body2">Host: {devInfo.database.host}:{devInfo.database.port}</Typography>
                )}
                <Typography variant="body2">Status: {getDbStatusText(devInfo.database.readyState)}</Typography>
              </Box>
              
              <Box>
                <Typography variant="subtitle2" gutterBottom>Server</Typography>
                <Typography variant="body2">Port: {devInfo.server.port}</Typography>
                <Typography variant="body2">Environment: {devInfo.environment}</Typography>
                {devInfo.server.allowedOrigins && (
                  <Typography variant="body2">Origins: {devInfo.server.allowedOrigins}</Typography>
                )}
              </Box>
            </Box>
          </Paper>
        )}
      </Collapse>
    </Box>
  );
};

export default DevInfo;